/**
 * AdminPlayersPage — /leagues/:leagueId/players
 *
 * Admin-only roster view for the selected league.
 * Gating: only League Admin or Super Admin of the selected league may view this
 * page. Any other authenticated user sees an "Access denied" message.
 *
 * Backend limitation note:
 * There is no dedicated "list players in a league" endpoint. This page uses
 * GET /v1/users (search all users in the tenant) combined with
 * GET /v1/leagues/:leagueId/administrators to derive admin role badges per row.
 * As a result it shows ALL tenant users, not just those with active teams in
 * this specific league. A league-scoped player list endpoint
 * (GET /v1/leagues/:leagueId/players) does not yet exist in the backend.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../store';
import { UserRole, Gender } from '@sulis-liiga/common';
import type { User } from '@sulis-liiga/common';
import {
  fetchLeagueAdministrators,
  assignLeagueAdministrator,
  revokeLeagueAdministrator,
  fetchLeague,
  clearAdminError,
} from '../../store/slices/leagueSlice';
import userService from '../../services/userService';
import { resendVerification } from '../../store/slices/authSlice';
import { useActiveLeagueTenant } from '../../hooks/useActiveLeagueTenant';

// ─── Types ────────────────────────────────────────────────────────────────────

type RoleFilter = 'all' | 'admin' | 'player';
type StatusFilter = 'all' | 'active' | 'unverified' | 'inactive';

interface ConfirmDialog {
  type: 'assign_admin' | 'revoke_admin' | 'deactivate' | 'reactivate' | 'resend_verification';
  userId: string;
  userName: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(firstName: string, lastName: string): string {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
}

function getUserStatus(user: User): 'active' | 'unverified' | 'inactive' {
  if (user.status === 'inactive') return 'inactive';
  if (!user.emailVerified) return 'unverified';
  return 'active';
}

function formatGender(gender: Gender): string {
  if (gender === Gender.MALE) return 'Male';
  if (gender === Gender.FEMALE) return 'Female';
  return 'Other';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const RoleBadge: React.FC<{ isLeagueAdmin: boolean }> = ({ isLeagueAdmin }) => {
  if (isLeagueAdmin) {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full text-white bg-violet-600 font-medium">
        League Admin
      </span>
    );
  }
  return (
    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">
      Player
    </span>
  );
};

const StatusBadge: React.FC<{ status: 'active' | 'unverified' | 'inactive' }> = ({ status }) => {
  if (status === 'active') {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-success-100 text-success-800 font-medium">
        Active
      </span>
    );
  }
  if (status === 'unverified') {
    return (
      <span
        className="text-xs px-2 py-0.5 rounded-full font-medium"
        style={{ background: '#fef3c7', color: '#92400e' }}
      >
        Unverified
      </span>
    );
  }
  return (
    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">
      Inactive
    </span>
  );
};

// ─── Skeleton row ─────────────────────────────────────────────────────────────

const SkeletonRow: React.FC = () => (
  <tr className="animate-pulse">
    <td className="px-4 py-3">
      <div className="flex items-center gap-2">
        <div className="h-7 w-7 rounded-full bg-gray-200" />
        <div className="h-4 w-32 rounded bg-gray-200" />
      </div>
    </td>
    <td className="px-4 py-3">
      <div className="h-4 w-40 rounded bg-gray-200" />
    </td>
    <td className="px-4 py-3">
      <div className="h-4 w-16 rounded bg-gray-200" />
    </td>
    <td className="px-4 py-3">
      <div className="h-4 w-24 rounded bg-gray-200" />
    </td>
    <td className="px-4 py-3">
      <div className="h-5 w-16 rounded-full bg-gray-200" />
    </td>
    <td className="px-4 py-3">
      <div className="h-5 w-16 rounded-full bg-gray-200" />
    </td>
    <td className="px-4 py-3" />
  </tr>
);

// ─── Row action menu ──────────────────────────────────────────────────────────

interface RowMenuProps {
  user: User;
  isLeagueAdmin: boolean;
  onAction: (type: ConfirmDialog['type'], user: User) => void;
}

const RowMenu: React.FC<RowMenuProps> = ({ user, isLeagueAdmin, onAction }) => {
  const [open, setOpen] = useState(false);
  const status = getUserStatus(user);

  return (
    <div className="relative">
      <button
        type="button"
        className="text-gray-400 hover:text-gray-600 px-2"
        aria-label="Row actions"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        ⋯
      </button>
      {open && (
        <>
          {/* Backdrop to close menu */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            className="absolute right-0 z-20 mt-1 w-56 bg-white rounded-md shadow-md border border-gray-100 text-sm"
            role="menu"
          >
            <div className="px-3 py-1.5 text-xs text-gray-400 border-b border-gray-100">
              Row actions
            </div>
            <Link
              to={`/profile`}
              className="block px-3 py-2 hover:bg-gray-50 text-gray-700"
              onClick={() => setOpen(false)}
              role="menuitem"
            >
              View profile
            </Link>
            {isLeagueAdmin ? (
              <button
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onAction('revoke_admin', user);
                }}
              >
                Remove admin
              </button>
            ) : (
              <button
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onAction('assign_admin', user);
                }}
              >
                Assign as league admin
              </button>
            )}
            {status === 'unverified' && (
              <button
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onAction('resend_verification', user);
                }}
              >
                Resend verification
              </button>
            )}
            {status === 'inactive' ? (
              <button
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-gray-50 text-success-600"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onAction('reactivate', user);
                }}
              >
                Reactivate
              </button>
            ) : (
              <button
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-gray-50 text-danger-600"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onAction('deactivate', user);
                }}
              >
                Deactivate
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Confirmation dialog ──────────────────────────────────────────────────────

interface ConfirmDialogModalProps {
  dialog: ConfirmDialog;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
  error: string | null;
}

const DIALOG_LABELS: Record<
  ConfirmDialog['type'],
  { title: string; body: (name: string) => string; confirmLabel: string; danger?: boolean }
> = {
  assign_admin: {
    title: 'Assign league admin',
    body: name => `Assign ${name} as a League Admin? They will be able to manage this league.`,
    confirmLabel: 'Assign',
  },
  revoke_admin: {
    title: 'Remove admin',
    body: name => `Remove ${name} as League Admin? They will lose admin access to this league.`,
    confirmLabel: 'Remove',
    danger: true,
  },
  deactivate: {
    title: 'Deactivate player',
    body: name =>
      `Deactivate ${name}? They will no longer be able to log in. This action is audit-logged.`,
    confirmLabel: 'Deactivate',
    danger: true,
  },
  reactivate: {
    title: 'Reactivate player',
    body: name => `Reactivate ${name}? They will regain the ability to log in.`,
    confirmLabel: 'Reactivate',
  },
  resend_verification: {
    title: 'Resend verification email',
    body: name =>
      `Resend the email verification link to ${name}? The previous link will be invalidated.`,
    confirmLabel: 'Resend',
  },
};

const ConfirmDialogModal: React.FC<ConfirmDialogModalProps> = ({
  dialog,
  onConfirm,
  onCancel,
  loading,
  error,
}) => {
  const meta = DIALOG_LABELS[dialog.type];
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h2 id="confirm-dialog-title" className="text-lg font-semibold text-gray-900">
          {meta.title}
        </h2>
        <p className="mt-2 text-sm text-gray-600">{meta.body(dialog.userName)}</p>
        {error && (
          <div className="mt-3 rounded-md bg-danger-50 p-3" role="alert">
            <p className="text-sm text-danger-800">{error}</p>
          </div>
        )}
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            className="px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50 ${
              meta.danger
                ? 'bg-danger-600 hover:bg-danger-700'
                : 'bg-primary-600 hover:bg-primary-700'
            }`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Processing…' : meta.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const AdminPlayersPage: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const dispatch = useDispatch<AppDispatch>();

  // Auth
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

  // League state
  const { currentLeague, administrators, adminError } = useSelector(
    (state: RootState) => state.league,
  );

  // Local state
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialog | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const PAGE_LIMIT = 20;

  // Derive admin set from league administrators
  const adminUserIds = new Set(administrators.map(a => a.id));

  // RBAC gate: current user must be League/Super Admin
  const isAdmin =
    user?.roles?.includes(UserRole.LEAGUE_ADMINISTRATOR) ||
    user?.roles?.includes(UserRole.SUPER_ADMINISTRATOR);

  // Resolve tenant slug from URL leagueId — gates data fetches to avoid registry leaks.
  const { resolved: tenantResolved } = useActiveLeagueTenant(leagueId);

  // Load league + admin list on mount
  useEffect(() => {
    if (leagueId && tenantResolved) {
      dispatch(fetchLeague(leagueId));
      dispatch(fetchLeagueAdministrators(leagueId));
    }
  }, [dispatch, leagueId, tenantResolved]);

  // Load users whenever search or page changes
  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const result = await userService.getAllUsers({
        search: search || undefined,
        page,
        limit: PAGE_LIMIT,
      });
      // getAllUsers returns { users, total, page, limit }
      setUsers(result.users ?? []);
      setTotalUsers(result.total ?? 0);
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Failed to load players.');
    } finally {
      setUsersLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      loadUsers();
    }
  }, [isAuthenticated, isAdmin, loadUsers]);

  // Filter locally by role and status (search is handled server-side)
  const filteredUsers = users.filter(u => {
    const isLeagueAdmin = adminUserIds.has(u.id);
    const status = getUserStatus(u);

    if (roleFilter === 'admin' && !isLeagueAdmin) return false;
    if (roleFilter === 'player' && isLeagueAdmin) return false;
    if (statusFilter !== 'all' && status !== statusFilter) return false;

    return true;
  });

  const totalPages = Math.ceil(totalUsers / PAGE_LIMIT);

  // ── Action handlers ────────────────────────────────────────────────────────

  const openConfirm = (type: ConfirmDialog['type'], u: User) => {
    setActionError(null);
    setConfirmDialog({
      type,
      userId: u.id,
      userName: `${u.firstName} ${u.lastName}`,
    });
  };

  const handleConfirm = async () => {
    if (!confirmDialog || !leagueId) return;
    setActionLoading(true);
    setActionError(null);

    try {
      const { type, userId, userName } = confirmDialog;

      if (type === 'assign_admin') {
        // Find the user's email for the assign thunk
        const u = users.find(x => x.id === userId);
        if (u) {
          await dispatch(assignLeagueAdministrator({ leagueId, email: u.email })).unwrap();
          setActionSuccess(`${userName} is now a League Admin.`);
        }
      } else if (type === 'revoke_admin') {
        await dispatch(revokeLeagueAdministrator({ leagueId, userId })).unwrap();
        setActionSuccess(`${userName} is no longer a League Admin.`);
      } else if (type === 'deactivate') {
        // Backend gap: no deactivate endpoint exists (PUT /v1/users/:id/status).
        // Show a friendly message instead of making a broken call.
        setActionError(
          'Deactivate is not yet supported by the backend. Contact a Super Admin to disable this account.',
        );
        setActionLoading(false);
        return;
      } else if (type === 'reactivate') {
        setActionError(
          'Reactivate is not yet supported by the backend. Contact a Super Admin to re-enable this account.',
        );
        setActionLoading(false);
        return;
      } else if (type === 'resend_verification') {
        const u = users.find(x => x.id === userId);
        if (u) {
          await dispatch(resendVerification(u.email)).unwrap();
          setActionSuccess(`Verification email resent to ${userName}.`);
        }
      }

      setConfirmDialog(null);
      // Reload to reflect changes
      await loadUsers();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Action failed. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = () => {
    dispatch(clearAdminError());
    setActionError(null);
    setConfirmDialog(null);
  };

  // ── Access guard ───────────────────────────────────────────────────────────

  if (!isAuthenticated) {
    return <Navigate to={`/login?returnTo=/leagues/${leagueId}/players`} replace />;
  }

  if (!isAdmin) {
    return (
      <div className="max-w-lg mx-auto mt-16 text-center">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Access denied</h1>
          <p className="text-gray-600 mb-6">
            This page is only available to League Admins and Super Admins of{' '}
            {currentLeague?.name ?? 'this league'}.
          </p>
          <Link
            to={leagueId ? `/leagues/${leagueId}` : '/'}
            className="inline-block px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            Back to league
          </Link>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const leagueName = currentLeague?.name ?? 'League';

  return (
    <>
      {/* Confirmation dialog */}
      {confirmDialog && (
        <ConfirmDialogModal
          dialog={confirmDialog}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          loading={actionLoading}
          error={actionError ?? adminError}
        />
      )}

      {/* Page header */}
      <div className="pb-4 border-b border-gray-200 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Players</h1>
          <p className="text-sm text-gray-500 mt-1">
            {leagueName}
            {totalUsers > 0 && ` · ${totalUsers} player${totalUsers !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs px-2 py-1 rounded-full text-white bg-violet-600 font-medium">
            Admin view
          </span>
        </div>
      </div>

      {/* Success banner */}
      {actionSuccess && (
        <div className="mt-4 rounded-md bg-green-50 p-4" role="status">
          <p className="text-sm font-medium text-green-800">{actionSuccess}</p>
        </div>
      )}

      {/* Filters */}
      <div className="mt-4 flex flex-wrap gap-3 items-center">
        <label htmlFor="player-search" className="sr-only">
          Search name or email
        </label>
        <input
          id="player-search"
          type="search"
          placeholder="Search name or email…"
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="rounded-md border border-gray-300 text-sm shadow-sm px-3 py-2 bg-white flex-1 min-w-[14rem]"
          aria-label="Search players by name or email"
        />
        <label htmlFor="role-filter" className="sr-only">
          Filter by role
        </label>
        <select
          id="role-filter"
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value as RoleFilter)}
          className="rounded-md border border-gray-300 text-sm shadow-sm px-3 py-2 bg-white"
          aria-label="Filter by role"
        >
          <option value="all">All roles</option>
          <option value="admin">League Admin</option>
          <option value="player">Player</option>
        </select>
        <label htmlFor="status-filter" className="sr-only">
          Filter by status
        </label>
        <select
          id="status-filter"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as StatusFilter)}
          className="rounded-md border border-gray-300 text-sm shadow-sm px-3 py-2 bg-white"
          aria-label="Filter by status"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="unverified">Unverified</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Admin error banner */}
      {adminError && !confirmDialog && (
        <div className="mt-4 rounded-md bg-danger-50 p-4" role="alert">
          <p className="text-sm font-medium text-danger-800">{adminError}</p>
        </div>
      )}

      {/* Players table */}
      <div className="mt-6 bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm" aria-label="Players list">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Player</th>
              <th className="text-left px-4 py-2 font-medium">Email</th>
              <th className="text-left px-4 py-2 font-medium">Gender</th>
              <th className="text-left px-4 py-2 font-medium">Role</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium" aria-label="Actions" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {/* Loading state */}
            {usersLoading && users.length === 0 && (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            )}

            {/* Error state */}
            {usersError && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center">
                  <p className="text-danger-600 text-sm">{usersError}</p>
                  <button
                    type="button"
                    onClick={() => loadUsers()}
                    className="mt-3 text-sm text-primary-600 hover:underline"
                  >
                    Try again
                  </button>
                </td>
              </tr>
            )}

            {/* Empty state — no data at all */}
            {!usersLoading && !usersError && users.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center">
                  <p className="text-gray-500 text-sm">No players found in this league yet.</p>
                </td>
              </tr>
            )}

            {/* Empty state — filters eliminated all rows */}
            {!usersLoading && !usersError && users.length > 0 && filteredUsers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center">
                  <p className="text-gray-500 text-sm">No players match your filters.</p>
                  <button
                    type="button"
                    onClick={() => {
                      setRoleFilter('all');
                      setStatusFilter('all');
                      setSearch('');
                    }}
                    className="mt-2 text-sm text-primary-600 hover:underline"
                  >
                    Clear filters
                  </button>
                </td>
              </tr>
            )}

            {/* Data rows */}
            {!usersError &&
              filteredUsers.map(u => {
                const isLeagueAdmin = adminUserIds.has(u.id);
                const status = getUserStatus(u);
                return (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">
                      <div className="flex items-center gap-2">
                        <span className="h-7 w-7 rounded-full bg-primary-200 text-primary-700 flex items-center justify-center text-xs font-semibold select-none">
                          {getInitials(u.firstName, u.lastName)}
                        </span>
                        <span>
                          {u.firstName} {u.lastName}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{u.email}</td>
                    <td className="px-4 py-3 text-gray-700">{formatGender(u.gender)}</td>
                    <td className="px-4 py-3">
                      <RoleBadge isLeagueAdmin={isLeagueAdmin} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <RowMenu
                        user={u}
                        isLeagueAdmin={isLeagueAdmin}
                        onAction={(type, target) => openConfirm(type, target)}
                      />
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              className="px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              className="px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <p className="mt-4 text-xs text-gray-400">
        Visible only to League Admins / Super Admins of this league. Mutating actions are confirmed
        and audit-logged.
      </p>
    </>
  );
};

export default AdminPlayersPage;
